// ✅ ViewBookingPage.jsx
import "./viewBooking.css";
import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import usersRequest from "../../request/usersRequest";
import scheduleBasedRequest from "../../request/scheduleBasedRequest";
import generalRequest from "../../request/generalRequest";
import BookingModal from "../bookingModal/bookingModal";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import ViewBookingSkeleton from "./viewBookingSkeleton/viewBookingSkeleton";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";
import BannerMessage from "../common/bannerMessage/bannerMessage";

const ViewBookingPage = () => {
	const [booking, setBooking] = useState({});
	const [review, setReview] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [shopData, setShopData] = useState(null);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [rescheduleData, setRescheduleData] = useState(null);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

	const { bookingId } = useParams();

	const navigate = useNavigate();
	const location = useLocation();

	const { language, userData } = useContext(CombinedContext);

	const isLoggedIn = !!(userData && userData.cognitoTokens?.IdToken);

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		if (location.state?.reviewSubmitted) {
			showBanner(
				"success",
				location.state.isEdit ? translations[language].viewBooking.misc.reviewUpdated : translations[language].viewBooking.misc.reviewSubmitted,
			);
		}
	}, []);

	useEffect(() => {
		async function loadBooking() {
			try {
				const data = await usersRequest.getBooking(bookingId);
				setBooking(data.booking || {});
				setReview(data.review || null);
			} catch (err) {
				setError("Failed to load booking");
				console.error(err);
			} finally {
				setLoading(false);
			}
		}
		loadBooking();
	}, [bookingId]);

	if (loading) return <ViewBookingSkeleton />;
	if (error) return <div className="error">{error}</div>;

	const addressQuery = encodeURIComponent(`${booking.address || ""}, ${booking.city || ""}, ${booking.country || ""}`);

	async function handleReschedule() {
		try {
			const res = await generalRequest.getShopWithstaffsByIdPublic(booking.shopId);
			setShopData(res.shop);
			setRescheduleData(booking);
			setIsBookingOpen(true);
		} catch (err) {
			console.error("Reschedule failed:", err);
			showBanner("error", translations[language].viewBooking?.rescheduleError || "Unable to reschedule booking");
		}
	}

	async function reloadBooking() {
		try {
			const data = await usersRequest.getBooking(bookingId);
			setBooking(data.booking || {});
			setReview(data.review || null);
		} catch (err) {
			console.error("Failed to reload booking", err);
		}
	}

	const renderStars = (rating) => {
		const stars = [];

		for (let i = 1; i <= 5; i++) {
			stars.push(
				<span key={i} className={i <= rating ? "sf-star sf-star-filled" : "sf-star sf-star-empty"}>
					<Star size={24} fill="currentColor" strokeWidth={0.5} />
				</span>,
			);
		}

		return stars;
	};

	async function confirmCancelBooking() {
		try {
			await usersRequest.cancelBookingCustomer(booking.bookingId);

			setBooking({ ...booking, status: "cancelled" });
			setConfirmCancelOpen(false);

			setTimeout(() => {
				if (isLoggedIn) {
					navigate("/bookings");
				} else {
					navigate(`/shops/${booking.shopId}` || "/");
				}
			}, 800);
		} catch (err) {
			console.error("Cancel failed:", err);
			showBanner("error", translations[language].viewBooking?.errors?.cancel || "Failed to cancel booking");
		}
	}

	return (
		<div className="sf-booking-container">
			{/* ✅ Header */}
			<div className="sf-booking-header">
				<button className="sf-back-btn" onClick={() => navigate(-1)}>
					<ArrowLeft size={20} />
				</button>
				<div className="sf-booking-title">
    {dayjs(booking.startTime).format("h:mm A")} • {dayjs(booking.date).locale(language).format("dddd, MMMM D")}
</div>
			</div>

			{/* ✅ Status */}
			<div className={`sf-status-badge ${booking.status || ""}`}>{translations[language].viewBooking.status[booking.status] || booking.status}</div>

			{/* ✅ Map Section */}
			<div className="sf-map-container">
				<ShopMapPreview lat={booking.lat} lng={booking.lng} />
			</div>
			<a
				className="sf-shop-directions-btn"
				href={`https://www.google.com/maps/dir/?api=1&destination=${addressQuery}`}
				target="_blank"
				rel="noopener noreferrer"
			>
				{translations[language].shopPage.sidebar.getDirections}
			</a>

			<div className="sf-shop-name-booking">{booking.shopName}</div>
			{/* ✅ Booking Details */}
			<div className="sf-details">
				<div className="detail-row">
					<span>{translations[language].viewBooking.details.services}</span>
					<span>
						{Array.isArray(booking.services) ? booking.services.map((s) => s.serviceNameEn || s.serviceNameEs || s.name).join(", ") : "N/A"}
					</span>
				</div>
				<div className="detail-row">
					<span>{translations[language].viewBooking.details.staff}</span>
					<span>{booking.staffName || "N/A"}</span>
				</div>
				<div className="detail-row">
					<span>{translations[language].viewBooking.details.time}</span>
					<span>
						{booking.startTime && booking.endTime
							? `${dayjs.tz(booking.startTime, booking.timeZone || "America/Santo_Domingo").format("h:mm A")} – ${dayjs
									.tz(booking.endTime, booking.timeZone || "America/Santo_Domingo")
									.format("h:mm A")}`
							: "N/A"}
					</span>
				</div>
			</div>

			{/* ✅ Buttons */}
			{booking.status !== "completed" && (
				<div className="sf-actions">
					<button className="btn-cancel" onClick={() => setConfirmCancelOpen(true)}>
						{translations[language].viewBooking.manage.cancelBtn}
					</button>
					<button className="btn-change" onClick={handleReschedule}>
						{translations[language].viewBooking.manage.rescheduleBtn}
					</button>
				</div>
			)}

			{booking.status === "completed" && review && (
				<div className="sf-view-booking-review-section">
					<h2 className="sf-view-booking-review-title">{translations[language].viewBooking.reviewTitle || "Your Review"}</h2>

					<div className="sf-view-booking-review-item">
						{/* HEADER */}
						<div className="sf-view-booking-review-header">
							<div className="sf-view-booking-review-name">{review.customerName || "Anonymous"}</div>

							<div className="sf-view-booking-review-rating">{renderStars(review.rating)}</div>
						</div>

						{/* SERVICES */}
						{review.services?.length > 0 && (
							<div className="sf-view-booking-review-services">
								{review.services.map((srv, i) => (
									<div key={i} className="sf-view-booking-review-service-chip">
										{language === "es" ? srv.serviceNameEs : srv.serviceNameEn}
										&nbsp;•&nbsp;
										<span>{srv.duration} min</span>
									</div>
								))}
							</div>
						)}

						{/* COMMENT */}
						<div className="sf-view-booking-review-comment">{review.comment || "No comment provided."}</div>

						{/* DATE */}
						<div className="sf-view-booking-review-date">{new Date(review.createdAt).toLocaleDateString()}</div>

						{/* ✅ EDIT REVIEW BUTTON */}
						<button
							className="btn brand"
							onClick={() =>
								navigate(`/reviews/${booking.bookingId}`, {
									state: {
										mode: "edit",
										reviewId: review.reviewId,
										review, // optional, but handy
									},
								})
							}
						>
							{language === "es" ? "Editar reseña" : "Edit review"}
						</button>
					</div>
				</div>
			)}

			{booking.status === "completed" && !review && (
				<div className="sf-view-booking-review-cta">
					<button className="sf-review-btn" onClick={() => navigate(`/reviews/${booking.bookingId}`)}>
						{language === "es" ? "Dejar una reseña" : "Leave a review"}
					</button>
				</div>
			)}

			{/* ✅ Reschedule Modal */}
			{isBookingOpen && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => {
						setIsBookingOpen(false);
						reloadBooking(); // wait for modal animation to finish
					}}
					availabilityId={shopData.shopId}
					shopData={shopData}
                    initialServices={booking.services}
					initialstaffId={booking.staffId}
					mode="reschedule"
					initialPhone={booking.phone || userData?.phone || ""}
					existingBooking={rescheduleData}
				/>
			)}

			{confirmCancelOpen && (
				<div className="sf-confirm-overlay" onClick={() => setConfirmCancelOpen(false)}>
					<div className="sf-confirm-modal" onClick={(e) => e.stopPropagation()}>
						<h3 className="sf-confirm-title">{language === "es" ? "¿Cancelar cita?" : "Cancel booking?"}</h3>

						<p className="sf-confirm-text">
							{language === "es"
								? "Esta acción no se puede deshacer. ¿Seguro que quieres cancelar tu cita?"
								: "This action cannot be undone. Are you sure you want to cancel this booking?"}
						</p>

						<div className="sf-confirm-actions">
							<button className="sf-confirm-secondary" onClick={() => setConfirmCancelOpen(false)}>
								{language === "es" ? "Volver" : "Go back"}
							</button>

							<button className="sf-confirm-danger" onClick={confirmCancelBooking}>
								{language === "es" ? "Sí, cancelar" : "Yes, cancel"}
							</button>
						</div>
					</div>
				</div>
			)}

			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</div>
	);
};

export default ViewBookingPage;
